<template>
  <div class="user-permission-table-wrapper">
    <!-- Loading State: Shimmer Table -->
    <lb-datatablenew
      v-if="loading"
      :headers="headers"
      :items="[{id:1},{id:2},{id:3},{id:4},{id:5}]"
      :enableslot="headers.map(h => h.value)"
      class="datatable-home-page"
      :hide-default-footer="true"
      :loading="false"
    >
      <template v-for="header in headers" v-slot:[header.value]>
         <v-skeleton-loader :key="header.value" type="text" class="my-2"></v-skeleton-loader>
      </template>
    </lb-datatablenew>

    <!-- Actual Data Table -->
    <div v-else-if="items.length > 0">
      <lb-datatablenew
        :key="tableKey"
        data-testid="user-permission__datatable"
        class="datatable-home-page"
        :headers="computedHeaders"
        :items="paginatedItems"
        :enableslot="['indexno', 'status']"
        :loading="false"
        :hide-default-footer="true"
        :bypass-filtering="true"
        @update:filters="handleFilterUpdate"
      >
        <template v-slot:indexno="{ item }">
          <div class="text-center">{{ item.indexno }}</div>
        </template>
        <template v-slot:status="{ item }">
          <StatusIndicator
            :variant="getStatusVariant(item.status)"
            :custom-label="item.status"
            size="sm"
          />
        </template>
      </lb-datatablenew>
      <Pagination
        :list="filteredItemsWithIndex"
        :itemsperpage="itemsPerPage"
        @paginated-data="updatePaginatedData"
      />
    </div>
  </div>
</template>

<script>
import StatusIndicator from '../../../components/StatusIndicator.vue';
import Pagination from '../../../../../views/common/Pagination.vue';
import { STATUS_VARIANTS } from '../../../utils/statusConstants';

export default {
  name: 'UserPermissionTable',
  components: {
    StatusIndicator,
    Pagination,
  },
  props: {
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      paginatedItems: [],
      activeFilters: {},
      itemsPerPage: 25, // Default to 25
    };
  },
  computed: {
    tableKey() {
      // Force re-render if items length dramatically changes, e.g. from 0 to N
      // This ensures the table initializes with the full list of filters
      return `table-${this.items.length}`;
    },
    computedHeaders() {
      if (!this.items || !this.items.length) return this.headers;
      
      // Clone headers and inject filterList derived from ALL items
      return this.headers.map((h) => {
        const newHeader = { ...h };
        if (newHeader.filterable) {
          const values = new Set();
          this.items.forEach((i) => {
            const val = i[newHeader.value];
            if (val !== undefined && val !== null) {
              values.add(String(val));
            }
          });
          
          // Create filter options from the unique values found in ALL items
          newHeader.filterList = Array.from(values).sort().map((v) => ({ 
            name: v, 
            value: v 
          }));
        }
        return newHeader;
      });
    },
    filteredItems() {
      let data = [...this.items]; // Work on a copy
      
      Object.keys(this.activeFilters).forEach((key) => {
        const filters = this.activeFilters[key];
        if (filters && filters.length > 0) {
          data = data.filter((item) => {
             const itemVal = item[key];
             const strVal = (itemVal !== undefined && itemVal !== null) ? String(itemVal) : '';
             return filters.includes(strVal);
          });
        }
      });
      return data;
    },
    filteredItemsWithIndex() {
      // Re-index items based on the filtered result
      // This ensures S.No starts from 1 and is continuous for the filtered list
      return this.filteredItems.map((item, index) => ({
        ...item,
        indexno: index + 1
      }));
    }
  },
  methods: {
    handleFilterUpdate(filters) {
      // Update local state with the filters selected in lb-datatablenew
      this.activeFilters = filters;
    },
    getStatusVariant(status) {
      return status === 'Active' ? STATUS_VARIANTS.ACTIVE : STATUS_VARIANTS.INACTIVE;
    },
    updatePaginatedData(payload) {
      this.paginatedItems = payload.data;
    },
  },
};
</script>
